import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { WorkflowRun } from '../../useWorkflowRuns';
import { RollbackOptionsValue } from './types';
import { useRollbackCloudbuild } from './useRollbackCloudbuild';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { prepareRollbackOptions } from './utils';
import { CloudbuildRollbackTableView } from './CloudbuildRollbackTableView';

export const CloudbuildRollbackDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  runs?: WorkflowRun[];
  projectId: string;
  location: string;
}> = ({ open, onClose, runs, projectId, location }) => {
  const [rollbackOptions, setRollbackOptions] = useState<Record<
    string,
    RollbackOptionsValue[]
  > | null>(null);
  const [selectedItems, setSelectedItems] = useState<RollbackOptionsValue[]>(
    [],
  );
  const { isLoading, error, executeRollback } = useRollbackCloudbuild();

  useEffect(() => {
    if (runs) {
      setRollbackOptions(prepareRollbackOptions(runs));
    }
  }, [runs]);

  const handleSelectionChange = (selected: RollbackOptionsValue[]) => {
    setSelectedItems(selected);
  };

  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const handleSubmit = async () => {
    const result = await executeRollback({
      projectId,
      location,
      rollbackOptions: selectedItems,
    });
    if (error) {
      // Not even partial success
      errorApi.post(error);
    } else {
      // Might be partially success
      alertApi.post({
        message: `Rollback triggered successfully for ${
          result?.summary.succeeded
        } item(s). ${
          result?.summary.failed
            ? `Failed for ${result?.summary.failed} item(s).`
            : ''
        } Pleas reload workflow runs to see the newly triggered build(s).`,
        severity: 'success',
      });
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      aria-label="Run trigger drawer"
    >
      <Box p={3} width={800} role="form" aria-label="Trigger execution form">
        <Box paddingY={3}>
          <Typography variant="h6">Rollback Deployment</Typography>
        </Box>

        <CloudbuildRollbackTableView
          rollbackOptions={rollbackOptions}
          onSelectionChange={handleSelectionChange}
        />

        <Box mt={3}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || selectedItems.length === 0}
            fullWidth
            aria-label="Run rollback"
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <Box ml={1}>Running rollback...</Box>
              </>
            ) : (
              `Rerun ${selectedItems.length} selected item${
                selectedItems.length !== 1 ? 's' : ''
              }`
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
